// People's time zones (erecords/usertz.go), set by the Entra sync from each
// person's contact information. Read-only in the UI.
import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { pathPrefix } from '../env'

const cache: Record<string, Promise<Record<string, string>>> = {}

function load(what: 'timezones' | 'places'): Promise<Record<string, string>> {
  if (!cache[what]) {
    cache[what] = fetch(pathPrefix + '/api/v2/erecords/' + what, {
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}))
  }
  return cache[what]
}

function useMap(what: 'timezones' | 'places'): Record<string, string> {
  const [m, setM] = useState<Record<string, string>>({})
  useEffect(() => {
    load(what).then(setM)
  }, [what])
  return m
}

// useTimezones returns {userID: zone} for everyone who has one.
export const useTimezones = (): Record<string, string> => useMap('timezones')

// usePlaces returns {userID: "Sammamish, WA, United States"} from Entra.
export const usePlaces = (): Record<string, string> => useMap('places')

// zoneName is a zone as people say it: America/Los_Angeles -> "Pacific Time",
// Asia/Tbilisi -> "Georgia Time" (the IANA name only names one city).
export function zoneName(zone: string): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longGeneric',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')
    if (part?.value && !/^GMT/.test(part.value)) return part.value
  } catch {
    // unknown zone or old browser: fall back to the city in the zone name
  }
  return (zone.split('/').pop() ?? zone).replace(/_/g, ' ')
}

// placeCity is the city of an Entra place, or the zone's city without one.
export const placeCity = (zone: string, place?: string): string =>
  place?.split(',')[0].trim() ||
  (zone.split('/').pop() ?? zone).replace(/_/g, ' ')

// theirTime renders a time range in someone's zone, e.g. "Tue 27 Oct 10:00 → 19:00".
export function theirTime(
  startISO: string,
  endISO: string,
  zone: string,
): string {
  const s = DateTime.fromISO(startISO).setZone(zone)
  const e = DateTime.fromISO(endISO).setZone(zone)
  const end = e.hasSame(s, 'day')
    ? e.toFormat('HH:mm')
    : e.toFormat('ccc d LLL HH:mm')
  return `${s.toFormat('ccc d LLL HH:mm')} → ${end}`
}
